import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { isFounder } from '../../config/founders';
import {
  IHome,
  IBook,
  IChart,
  IKey,
  IPlay,
  IHash,
  IShield,
  IX,
  ILogout,
} from '../../pages/Dashboard/DashIcons';

// Sidebar now matches the monochrome dashboard design: brand at top,
// Workspace / Ops sections with keyboard-shortcut hints, and a user
// footer with an avatar, email, and orgId. The sign-out action lives
// in a small menu that opens above the user footer.
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const workspace = [
    { to: '/dashboard', label: 'Home', kbd: 'H', Icon: IHome },
    { to: '/memories', label: 'Memories', kbd: 'M', Icon: IBook },
    { to: '/playground', label: 'Playground', kbd: 'P', Icon: IPlay },
    { to: '/tags', label: 'Tags', kbd: 'T', Icon: IHash },
    { to: '/usage', label: 'Usage', kbd: 'U', Icon: IChart },
    { to: '/api-key', label: 'API key', kbd: 'K', Icon: IKey },
  ];
  const ops = isFounder(user?.email)
    ? [{ to: '/admin', label: 'Admin', kbd: 'A', Icon: IShield }]
    : [];

  const isActive = (to) => {
    if (to === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname === to;
  };

  // Close the user menu on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const email = user?.email || user?.name || 'User';
  const initial = (email || 'U').trim().charAt(0).toUpperCase();
  const orgId = user?.orgId || 'org';

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
      toast.success('Signed out');
      navigate('/');
    } catch {
      toast.error('Sign out failed');
    }
  };

  const handleNavClick = () => {
    // On mobile the sidebar is a drawer — collapse it after nav.
    onClose?.();
  };

  return (
    <>
      <div
        className={`side-backdrop ${isOpen ? 'on' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`side ${isOpen ? 'open' : ''}`}>
        <div className="side-brand">
          <span className="brand-mark">M</span>
          <span className="name">MME</span>
          <span className="env">prod</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close sidebar"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--ink-4)',
              cursor: 'pointer',
              marginLeft: 4,
              display: 'none',
            }}
            className="mobile-close"
          >
            <IX size={16} />
          </button>
        </div>

        <div className="side-section">Workspace</div>
        <div className="side-links">
          {workspace.map((item) => {
            const { Icon } = item;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={handleNavClick}
                className={`side-link ${isActive(item.to) ? 'active' : ''}`}
              >
                <Icon />
                <span className="lbl">{item.label}</span>
                <span className="kbd">{item.kbd}</span>
              </Link>
            );
          })}
        </div>

        {ops.length > 0 && (
          <>
            <div className="side-section">Ops</div>
            <div className="side-links">
              {ops.map((item) => {
                const { Icon } = item;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={handleNavClick}
                    className={`side-link ${isActive(item.to) ? 'active' : ''}`}
                  >
                    <Icon />
                    <span className="lbl">{item.label}</span>
                    <span className="kbd">{item.kbd}</span>
                  </Link>
                );
              })}
            </div>
          </>
        )}

        <div className="side-user" ref={menuRef}>
          <span className="av">{initial}</span>
          <div className="meta">
            <strong title={email}>{email}</strong>
            <span title={orgId}>{orgId}</span>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="side-user-btn"
            aria-label="Account menu"
            aria-expanded={menuOpen}
          >
            <ILogout size={14} />
          </button>
          {menuOpen && (
            <div className="user-menu" role="menu">
              <button type="button" className="user-menu-item" onClick={handleLogout}>
                <ILogout size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
